import { store } from "@/store/store";
import { getStorageImagePath } from "@/utils/utils"
import { Helmet } from "react-helmet-async"
import { useLocation } from "react-router-dom"




export function generateStructuredData({
    pageType = "page",
    // General page data
    title,
    description,
    url,
    image,
    // Website / company data
    siteName,
    siteUrl,
    logo,
    sameAs,
    // Service page data
    serviceType,
    areaServed = "Worldwide",
    // Contact page data
    email,
    telephone,
    // Blog data
    authorName,
    publishedDate,
    modifiedDate,
    keywords ,
}) {
    const organization = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteName,
        url: siteUrl,
        logo,
        sameAs,
    };

    const website = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteName,
        url: siteUrl,
    };

    if (pageType === "home") {
        return [organization, website];
    }

    if (pageType === "service") {
        return {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType,
            name: title,
            url,
            description,
            areaServed,
            provider: {
                "@type": "Organization",
                name: siteName,
                url: siteUrl,
                logo,
                sameAs,
            },
        };
    }

    if (pageType === "blog") {
        return {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: title,
            name: title,
            description,
            url,
            ...(image && { image }),
            ...(publishedDate && { datePublished: publishedDate }),
            ...(modifiedDate && { dateModified: modifiedDate }),
            author: {
                "@type": "Person",
                name: authorName,
            },
            publisher: {
                "@type": "Organization",
                name: siteName,
                logo,
            },
            keywords: keywords,
        };
    }

    if (pageType === "contact") {
        return {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: title,
            url,
            description,
            mainEntity: {
                "@type": "Organization",
                name: siteName,
                url: siteUrl,
                logo,
                email,
                telephone,
                sameAs,
            },
        };
    }

    if (pageType === "about") {
        return {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: title,
            url,
            description,
            mainEntity: {
                "@type": "Organization",
                name: siteName,
                url: siteUrl,
                logo,
                sameAs,
            },
        };
    }

    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        url,
        description,
        ...(image && { image }),
        publisher: {
            "@type": "Organization",
            name: siteName,
            url: siteUrl,
            logo,
        },
    };
}


export const SeoTags = ({ page, seo_settings, pageType = undefined }) => {
    const { pathname } = useLocation()


    const site_settings = store.getState().app.config.site_settings
    const getValue = (field) => {
        const pageValue = page?.[field]

        if (pageValue === null || pageValue === undefined || pageValue === "") {
            return seo_settings?.[field] ?? ""
        }

        return pageValue
    }

    const seoImage = getValue("seo_image")

    const seoImageAlt = (() => {
        if (!seoImage) {
            return ""
        }

        if (typeof seoImage === "string") {
            try {
                return JSON.parse(seoImage)?.alt ?? ""
            } catch {
                return ""
            }
        }

        return seoImage?.alt ?? ""
    })()

    const currentUrl = typeof window === "undefined"
        ? pathname
        : `${window.location.origin}${pathname}`

    const seoKeywords = getValue("seo_keywords")
    const publishedDate = getValue("created_at")
    const modifiedDate = getValue("updated_at")

    const structuredData = generateStructuredData({
        pageType,
        title: getValue("seo_title"),
        description: getValue("seo_description"),
        url: currentUrl,
        image: getStorageImagePath(seoImage),

        siteName: seo_settings?.seo_site_name,
        siteUrl: window.location.origin,
        logo: `${window.location.origin}/static/media/darperso-blue.d5f1151ef8589dc40e66246fff7bd82f.svg`,
        sameAs: [
            site_settings.facebook_url,
            site_settings.instagram_url,
            site_settings.linkedin_url,
        ],
        serviceType: getValue("seo_title"),
        areaServed:"Worldwide",

        email:site_settings?.email,
        telephone:site_settings?.phone_number,

        publishedDate,
        modifiedDate,
        keywords: typeof seoKeywords === "string"
            ? seoKeywords.split(",").map((keyword) => keyword.trim()).filter(Boolean)
            : [],
        authorName:getValue("author_name") ?? "Darperso Digital"
    });

    return <Helmet>
        <title>{getValue("seo_page_title")}</title>
        <meta name="title" content={getValue("seo_title")} />
        <meta name="keywords" content={seoKeywords} />
        <meta
            name="description"
            content={getValue("seo_description")}
        />
        <meta name="robots" content={getValue("seo_robots")} />
        <link rel="canonical" href={currentUrl} />
        <meta
            property="og:description"
            content={getValue("seo_description")}
        />
        <meta property="og:image" content={getStorageImagePath(seoImage)} />
        <meta property="og:image:alt" content={seoImageAlt} />
        <meta
            property="og:title"
            content={getValue("seo_title")}
        />
        <meta property="og:site_name" content={getValue("seo_site_name")} />
        <meta property="og:type" content={getValue("seo_og_type")} />
        <meta property="og:url" content={currentUrl} />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getValue("seo_title")} />
        <meta
            name="twitter:description"
            content={getValue("seo_description")}
        />
        <meta name="twitter:image" content={getStorageImagePath(seoImage)} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
}
